import type { Route } from 'vue-router';
import { entriesLoadTiming } from '@cognitoforms/utils/entries-load-marks';

export { ENTRIES_PERF_DEBUG_KEY, entriesLoadTiming } from '@cognitoforms/utils/entries-load-marks';

export const ENTRIES_ROWS_POPULATED_TIMEOUT_MS = 30000;

export function getLegacyCurrentViewNumber(): string | null {
	const viewId = (window as unknown as { Cognito?: { Forms?: { model?: { currentView?: { get_Id?(): string } } } } }).Cognito?.Forms?.model?.currentView?.get_Id?.();
	return /-(\d+)$/.exec(viewId ?? '')?.[1] ?? null;
}

/**
 * Determines whether a navigation will load an entries grid (as opposed to opening an entry or other in-page state change).
 */
export function isEntriesLoadNavigation(
	to: Pick<Route, 'name' | 'params'>,
	from: Pick<Route, 'name' | 'params'>,
	currentViewNumber: string | null = getLegacyCurrentViewNumber()
): boolean {
	if (to.name !== 'entries')
		return false;
	if (from.name !== 'entries' || to.params.formUrlName !== from.params.formUrlName)
		return true;
	const viewNumber = (entryView: string) => /^(\d+)-/.exec(entryView ?? '')?.[1] ?? null;
	const fromView = viewNumber(from.params.entryView);
	const toView = viewNumber(to.params.entryView);
	return !!fromView && !!toView && fromView !== toView && toView !== currentViewNumber;
}

export function beginEntriesViewChange(): void {
	if (!entriesLoadTiming.isActive() || entriesLoadTiming.hasMark('change-view'))
		entriesLoadTiming.begin('spa');
	entriesLoadTiming.mark('change-view');
}

export function cancelEntriesLoadUnlessViewChanged(loadId: string | null): void {
	if (loadId && loadId === entriesLoadTiming.loadId() && !entriesLoadTiming.hasMark('change-view'))
		entriesLoadTiming.cancel(loadId);
}

export type RowsPopulatedWait = {
	readonly loadId: string | null;
	bind(loadId: string | null | undefined): void;
	take(): string | null;
	abandon(): void;
	reset(): void;
};

/**
 * Tracks the load waiting for its grid rows to populate. If they have not populated `timeoutMs` after the grid binds,
 * `onTimeout` is called with the load id while that load is still active, so it can be reported instead of dropped.
 */
export function createRowsPopulatedWait(
	onTimeout: (loadId: string) => void,
	timeoutMs = ENTRIES_ROWS_POPULATED_TIMEOUT_MS
): RowsPopulatedWait {
	let pendingLoadId: string | null = null;
	return {
		get loadId() {
			return pendingLoadId;
		},
		bind(loadId) {
			if (!loadId || loadId !== entriesLoadTiming.loadId())
				return;
			pendingLoadId = loadId;
			setTimeout(() => {
				if (pendingLoadId === loadId)
					pendingLoadId = null;
				if (loadId === entriesLoadTiming.loadId())
					onTimeout(loadId);
			}, timeoutMs);
		},
		take() {
			const loadId = pendingLoadId;
			pendingLoadId = null;
			return loadId;
		},
		abandon() {
			pendingLoadId = null;
			entriesLoadTiming.cancel();
		},
		reset() {
			pendingLoadId = null;
		}
	};
}
